import {FormattableCurrency} from '@ttx/core';
import {
  ArrowRightIcon,
  Box,
  Button,
  GridContainerWithTitle,
  HorizontalDivider,
  Typography,
  VisibilityBox,
} from '@ttx/design-system';
import {useBreakpoint} from '@ttx/design-system/src/use-breakpoint';
import React, {FC, useEffect, useState} from 'react';
import {loadStripe} from '@stripe/stripe-js';
import {Elements} from '@stripe/react-stripe-js';
import {usePrivy} from '@privy-io/react-auth';
import {CreditCardForm, TransactionStatusEnum} from './credit-card-form';
import {Item} from './purchase-item';
import {StripeSuccessModel} from './stripe-success-model';
import {StripeErrorModel} from './stripe-error-model';
import {useToastContext} from '../../../../contexts/toast-context';
// eslint-disable-next-line import/no-cycle

interface ContractInfo {
  contractId: string;
  walletAddress: null | `0x${string}`;
  _listingId: string;
  _currency?: string;
  _price: string;
  _quantityToBuy: number;
  _buyer: null | `0x${string}`;
  _proof: string[];
  _signatureTimestamp: number;
  signature: string;
  price: string;
  currency?: FormattableCurrency | null;
}

interface PolymeshNftInfo {
  instructionId: string | undefined;
  currency: string | undefined;
  tokenId: string | undefined;
  priceInDollars: string | undefined;
  slug: string | undefined;
  buyerDid: string | undefined;
}

interface StripeCheckoutModalProps {
  onModalClose: () => void;
  imageSrc?: string;
  isMobile?: boolean;
  trackName?: string;
  artist?: string;
  quantity: number;
  onIncrement: () => void;
  onDecrement: () => void;
  total: string | null;
  transactionFee: string | null;
  contractInfo?: ContractInfo;
  polymeshNftInfo?: PolymeshNftInfo;
  isWidgetVisible: boolean;
  isLoading: boolean;
  network: string;
  isJoshSavage?: boolean;
  onTxnSuccess?: () => void;
}

export const StripeCheckoutModal: FC<StripeCheckoutModalProps> = ({
  onModalClose,
  imageSrc,
  trackName,
  artist,
  quantity,
  onIncrement,
  onDecrement,
  transactionFee,
  total,
  contractInfo,
  polymeshNftInfo,
  isWidgetVisible,
  isLoading,
  network,
  onTxnSuccess,
  ...props
}) => {
  const {user: privyUser} = usePrivy();
  const {currentBreakpoint} = useBreakpoint();
  const mobileView = currentBreakpoint === '768px';
  const stripe = loadStripe(
    process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY || '',
  );

  const [clientSecret, setClientSecret] = useState<string>('');
  const [transactionStatus, setTransactionStatus] =
    useState<TransactionStatusEnum>(TransactionStatusEnum.STARTED);
  const [trackingId, setTrackingId] = useState<string>('');
  const toast = useToastContext();

  useEffect(() => {
    if (!trackingId) return;

    const eventSource = new EventSource(
      `${process.env.NEXT_PUBLIC_STRIPE_BACKEND_URL}/sse/stripe-events`,
    );

    eventSource.onmessage = (event): void => {
      try {
        const {data} = JSON.parse(event.data);
        console.log('data', data, data.transactionId, trackingId, data.status);
        if (data.transactionId === trackingId) {
          switch (data.status) {
            case 'success': {
              toast.addSuccessToast('Transaction completed successfully');
              if (onTxnSuccess) onTxnSuccess();
              break;
            }
            case 'failed': {
              toast.addErrorToast('Transaction has been failed');
              break;
            }
            case 'refunded': {
              toast.addWarningToast('Transaction has been refunded');
              break;
            }
            case 'refund_failed': {
              toast.addErrorToast('Refund attempt failed');
              break;
            }
            default: {
              break;
            }
          }
        }
      } catch (error) {
        console.error('Error processing SSE message:', error);
      }
    };

    eventSource.onerror = (error) => {
      console.error('SSE Error:', error);
      eventSource.close();
    };
  }, [trackingId, onTxnSuccess]);

  // For Polymesh
  useEffect(() => {
    if (
      network === 'polymesh' &&
      transactionStatus === TransactionStatusEnum.SUCCESS &&
      onTxnSuccess
    ) {
      onTxnSuccess();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transactionStatus]);

  const onStripeCheckout = async () => {
    if (process.env.NEXT_PUBLIC_STRIPE_BACKEND_URL) {
      let metadata;
      let receiptEmail;

      if (!total || Number(total) <= 0) {
        toast.addErrorToast('Invalid purchase amount');
        return;
      }

      if (network === 'ethereum' || network === 'polygon') {
        metadata = {
          buyerWalletAddress: contractInfo?.walletAddress,
          buyoutPrice: contractInfo?._price,
          currency: contractInfo?._currency,
          listingId: contractInfo?._listingId,
          currentStamp: contractInfo?._signatureTimestamp,
          proof: contractInfo?._proof,
          purchaseQuantity: contractInfo?._quantityToBuy,
          sigHash: contractInfo?.signature,
          network,
        };
        receiptEmail = privyUser?.email?.address || null;
      } else if (network === 'polymesh') {
        // NFT DB data
        metadata = {
          instructionId: polymeshNftInfo?.instructionId,
          currency: polymeshNftInfo?.currency,
          tokenId: polymeshNftInfo?.tokenId,
          price: polymeshNftInfo?.priceInDollars,
          slug: polymeshNftInfo?.slug,
          buyerDid: polymeshNftInfo?.buyerDid,
          network,
        };
      }

      const resp = await fetch(
        `${process.env.NEXT_PUBLIC_STRIPE_BACKEND_URL}/stripe/intent`,
        {
          method: 'POST',
          headers: {'Content-Type': 'application/json'},
          body: JSON.stringify({
            amount: Math.round(Number(total) * 100), // converting to cents
            description: trackName as string,
            receipt_email: receiptEmail,
            metadata,
          }),
        },
      );
      if (resp.ok) {
        const {data} = await resp.json();
        setClientSecret(data.clientSecret);
        setTrackingId(data.trackingId);
      }
    } else {
      setTransactionStatus(TransactionStatusEnum.ERROR);
    }
  };

  return (
    <Box w="100%" maxWidth={[366, 568]} paddingX={['two', 'zero']}>
      <GridContainerWithTitle
        title={
          (network !== 'polymesh' &&
            transactionStatus === TransactionStatusEnum.SUCCESS) ||
          transactionStatus === TransactionStatusEnum.ERROR
            ? 'Transaction Complete'
            : 'Checkout'
        }
        onModalClose={onModalClose}
        backgroundColor="surfaceBlack"
      >
        <Item
          imageSrc={imageSrc}
          isMobile={mobileView}
          label={trackName as string}
          artist={artist as string}
          tokenId={polymeshNftInfo?.tokenId as string}
          quantity={quantity}
          hideQtyButton={
            transactionStatus === TransactionStatusEnum.SUCCESS ||
            transactionStatus === TransactionStatusEnum.ERROR
              ? true
              : isWidgetVisible
          }
          onIncrement={() => {
            if (isWidgetVisible) return;
            onIncrement();
          }}
          onDecrement={() => {
            if (isWidgetVisible) return;
            onDecrement();
          }}
        />

        {transactionStatus === TransactionStatusEnum.STARTED ||
        transactionStatus === TransactionStatusEnum.LOADING ? (
          <>
            {network === 'polymesh' ? (
              <>
                <Box paddingLeft="eight" paddingRight="eight">
                  <HorizontalDivider color="grey12" />
                </Box>
                <Box
                  paddingLeft="eight"
                  paddingRight="eight"
                  paddingTop="two"
                  paddingBottom="four"
                >
                  <Box flexDirection="row">
                    <Box flex="1">
                      <Typography fontSize="s" color="textLow">
                        Service fee (Stripe + Gas)
                      </Typography>
                      <Typography fontSize="h3" color="textDefault">
                        {Number.isNaN(Number(transactionFee))
                          ? '--'
                          : String(Number(transactionFee)?.toFixed(2))}
                      </Typography>
                    </Box>
                    <Box flex="1">
                      <Typography fontSize="s" color="textLow">
                        Total
                      </Typography>
                      <Typography fontSize="h3" color="textDefault">
                        {Number.isNaN(Number(total))
                          ? '--'
                          : String(Number(total)?.toFixed(2))}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </>
            ) : (
              !props.isJoshSavage && (
                <>
                  <Box paddingLeft="eight" paddingRight="eight">
                    <HorizontalDivider color="grey12" />
                  </Box>
                  <Box
                    paddingLeft="eight"
                    paddingRight="eight"
                    paddingTop="two"
                    paddingBottom="four"
                  >
                    <Box flexDirection="row">
                      <Box flex="1">
                        <Typography fontSize="s" color="textLow">
                          Service fee
                        </Typography>
                        <Typography fontSize="h3" color="textDefault">
                          {String(Number(transactionFee)?.toFixed(2))}
                        </Typography>
                      </Box>
                      <Box flex="1">
                        <Typography fontSize="s" color="textLow">
                          Total (includes Gas)
                        </Typography>
                        <Typography fontSize="h3" color="textDefault">
                          {`${String(Number(total)?.toFixed(2))}`}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </>
              )
            )}

            {!clientSecret ? (
              <Box
                flexDirection="row"
                borderWidth="one"
                borderTopWidth="zero"
                borderStyle="solid"
                borderColor="outlines"
                flex={1}
                bg="surfaceDark"
              >
                <Box flex={1}>
                  <VisibilityBox show={[true, true]}>
                    <Button
                      type="primary"
                      size="M"
                      onPress={onStripeCheckout}
                      buttonStyle={{margin: '8px 24px 16px 24px'}}
                      leftAccessory={<ArrowRightIcon color="textInvert" />}
                      loading={isLoading}
                      disabled={Number.isNaN(Number(total))}
                    >
                      Confirm purchase
                    </Button>
                  </VisibilityBox>
                </Box>
              </Box>
            ) : (
              <Box padding="eight">
                <Elements
                  options={{
                    clientSecret,
                    appearance: {theme: 'night'},
                  }}
                  stripe={stripe}
                >
                  {/* eslint-disable-next-line @typescript-eslint/no-use-before-define */}
                  <CreditCardForm
                    transactionStatus={transactionStatus}
                    setTransactionStatus={setTransactionStatus}
                  />
                </Elements>
              </Box>
            )}
          </>
        ) : (
          network !== 'polymesh' && (
            <>
              {transactionStatus === TransactionStatusEnum.SUCCESS && (
                <Box padding="eight">
                  <StripeSuccessModel isMobile={false} />
                </Box>
              )}
              {transactionStatus === TransactionStatusEnum.ERROR && (
                <Box padding="eight">
                  <StripeErrorModel isMobile={false} />
                </Box>
              )}
            </>
          )
        )}
      </GridContainerWithTitle>
    </Box>
  );
};
